import { TimelineEventSchema } from '@/timeline/events/store/schemas'
import { TimelineReferenceSchema } from '@/timeline/references/store/schemas'
import { parseISODate, parseTimestamp } from '@/util/zod/validators'
import { z } from 'zod'

// Mirrors backend paxton_app/timeline/asset/schemas.py + the update/file status enums. Keep the
// client-facing update shape separate from the database row: internal owner ids, requested file ids,
// workflow versions, and raw error details stay server-side.

export enum TimelineUpdateKind {
  INITIALIZE = 'INITIALIZE',
  ADD_FILES = 'ADD_FILES',
  REMOVE_FILE = 'REMOVE_FILE',
}

// PENDING -> EXTRACTING -> MERGING -> FINALIZING -> DONE is the happy path; FAILED_FILES holds
// the run until the user retries or completes excluding failures; ERROR left the live timeline
// untouched.
export enum TimelineUpdateStatus {
  PENDING = 'PENDING',
  EXTRACTING = 'EXTRACTING',
  MERGING = 'MERGING',
  FINALIZING = 'FINALIZING',
  DONE = 'DONE',
  FAILED_FILES = 'FAILED_FILES',
  ERROR = 'ERROR',
}

export const kActiveTimelineUpdateStatuses = [
  TimelineUpdateStatus.PENDING,
  TimelineUpdateStatus.EXTRACTING,
  TimelineUpdateStatus.MERGING,
  TimelineUpdateStatus.FINALIZING,
]

export enum TimelineAssetFileStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  EXTRACTED = 'EXTRACTED',
  INCLUDED = 'INCLUDED',
  FAILED = 'FAILED',
}

export enum FileAddDisposition {
  NEW = 'NEW',
  ALREADY_INCLUDED = 'ALREADY_INCLUDED',
  FAILED_WILL_RETRY = 'FAILED_WILL_RETRY',
  NOT_READY = 'NOT_READY',
  NOT_IN_MATTER = 'NOT_IN_MATTER',
}

export const TimelineUpdateReadSchema = z.object({
  id: z.string().uuid(),
  asset_id: z.string().uuid(),
  kind: z.nativeEnum(TimelineUpdateKind),
  status: z.nativeEnum(TimelineUpdateStatus),
  files_requested: z.number().int(),
  files_added: z.number().int().nullable(),
  events_added: z.number().int().nullable(),
  events_removed: z.number().int().nullable(),
  files_unchanged: z.number().int().nullable(),
  files_failed: z.number().int().nullable(),
  error_message: z.string().nullable(),
  completed_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})
export type TimelineUpdateRead = z.infer<typeof TimelineUpdateReadSchema>

export const TimelineUpdateHistoryResponseSchema = z.object({
  asset_id: z.string().uuid(),
  updates: z.array(TimelineUpdateReadSchema),
})
export type TimelineUpdateHistoryResponse = z.infer<typeof TimelineUpdateHistoryResponseSchema>

export const TimelineAssetOverviewSchema = z.object({
  id: z.string().uuid(),
  matter_id: z.string().uuid(),
  chronology_version: z.number().int(),
  workflow_version: z.number().int().nullable(),
  last_built_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  total_events: z.number().int(),
  total_files: z.number().int(),
  active_update: TimelineUpdateReadSchema.nullable(),
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})
export type TimelineAssetOverview = z.infer<typeof TimelineAssetOverviewSchema>

// Get-or-empty response: asset is null when the matter has no timeline yet.
export const TimelineAssetOverviewResponseSchema = z.object({
  asset: TimelineAssetOverviewSchema.nullable(),
})
export type TimelineAssetOverviewResponse = z.infer<typeof TimelineAssetOverviewResponseSchema>

export const TimelineAssetEventsResponseSchema = z.object({
  asset_id: z.string().uuid(),
  chronology_version: z.number().int(),
  total_events: z.number().int(),
  events: z.array(TimelineEventSchema),
  references: z.record(z.string().uuid(), TimelineReferenceSchema),
})
export type TimelineAssetEventsResponse = z.infer<typeof TimelineAssetEventsResponseSchema>

export const TimelineAssetFileViewSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  status: z.nativeEnum(TimelineAssetFileStatus),
  inclusion_index: z.number().int(),
  last_processed_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  events_extracted: z.number().int(),
  error_reason: z.string().nullable(),
})
export type TimelineAssetFileView = z.infer<typeof TimelineAssetFileViewSchema>

export const TimelineAssetFilesResponseSchema = z.object({
  asset_id: z.string().uuid(),
  files: z.array(TimelineAssetFileViewSchema),
})
export type TimelineAssetFilesResponse = z.infer<typeof TimelineAssetFilesResponseSchema>

export const PossibleDuplicateSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
})
export type PossibleDuplicate = z.infer<typeof PossibleDuplicateSchema>

export const FileAddPreviewItemSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string().nullable(),
  disposition: z.nativeEnum(FileAddDisposition),
  possible_duplicate_of: PossibleDuplicateSchema.nullable(),
})
export type FileAddPreviewItem = z.infer<typeof FileAddPreviewItemSchema>

export const AddFilesPreviewResponseSchema = z.object({
  asset_id: z.string().uuid(),
  items: z.array(FileAddPreviewItemSchema),
})
export type AddFilesPreviewResponse = z.infer<typeof AddFilesPreviewResponseSchema>

// Timeline removal-impact uses event-date field names (the per-feature read-path divergence from
// med-chron's date_of_service names).
export const RemovalImpactResponseSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  events_count: z.number().int(),
  earliest_event_date: z.preprocess((val, ctx) => parseISODate(val, ctx), z.string().nullable()),
  latest_event_date: z.preprocess((val, ctx) => parseISODate(val, ctx), z.string().nullable()),
})
export type RemovalImpactResponse = z.infer<typeof RemovalImpactResponseSchema>

export const RemoveFileResponseSchema = z.object({
  asset_id: z.string().uuid(),
  user_file_id: z.string().uuid(),
  events_removed: z.number().int(),
  total_events: z.number().int(),
  total_files: z.number().int(),
  chronology_version: z.number().int(),
})
export type RemoveFileResponse = z.infer<typeof RemoveFileResponseSchema>

export const InitializeTimelineResponseSchema = z.object({
  asset: TimelineAssetOverviewSchema,
  update: TimelineUpdateReadSchema,
})
export type InitializeTimelineResponse = z.infer<typeof InitializeTimelineResponseSchema>

export const ApplyUpdateResponseSchema = z.object({
  update: TimelineUpdateReadSchema,
})
export type ApplyUpdateResponse = z.infer<typeof ApplyUpdateResponseSchema>

export const UpdateProgressSchema = z.object({
  files_total: z.number().int(),
  files_pending: z.number().int(),
  files_processing: z.number().int(),
  files_extracted: z.number().int(),
  files_included: z.number().int(),
  files_failed: z.number().int(),
})
export type UpdateProgress = z.infer<typeof UpdateProgressSchema>

export const FailedFileSummarySchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  error_reason: z.string().nullable(),
})
export type FailedFileSummary = z.infer<typeof FailedFileSummarySchema>

// update.id doubles as the user-facing error ID when the run ends in ERROR.
export const UpdateStatusResponseSchema = z.object({
  update: TimelineUpdateReadSchema,
  progress: UpdateProgressSchema,
  failed_files: z.array(FailedFileSummarySchema),
})
export type UpdateStatusResponse = z.infer<typeof UpdateStatusResponseSchema>

// Request payloads (validated server-side; plain types here).
export type InitializeTimelineRequest = {
  matter_id: string
  user_file_ids: string[]
}

export type AddFilesPreviewRequest = {
  user_file_ids: string[]
}

export type ApplyUpdateRequest = {
  add_user_file_ids: string[]
}
